import React from 'react';
import styled from 'styled-components';
import { getTranslatedString } from 'helpers/i18n';
import { colors } from 'helpers/ColorHelper';
import { media } from 'helpers/MediaHelper';
import { CHARGE_TYPE_MAPPING } from '../../constants';

import RadioGroup from '../common/RadioGroup';
import AdditionalServices from '../review_renewal_details/AdditionalServices';
import ServiceDetails from './services_and_utilities/ServiceDetails';
import UtilityDetails from './services_and_utilities/UtilityDetails';
import { Card, Header, BoldTitle, HorizontalDivider } from '../common/Card';

const StyledRadioUnderline = styled.div`
  display: flex;
  border-bottom: 2px solid ${colors.LIGHT_GREY};
  width: ${props => (props.expand ? '100%' : '50%')};
  ${media.mobile`
    flex-direction: column;
    width: 100%;
  `};
`;

const ServicesAndUtilitiesCard = ({ values, errors, touched, setFieldValue }) => {
  const { services, utilities } = values;

  return (
    <Card>
      <Header>
        <BoldTitle>
          {getTranslatedString('landlordPortal.rentRoll.renewals.servicesAndUtilities')}
        </BoldTitle>
      </Header>
      <HorizontalDivider />
      <BoldTitle>
        {getTranslatedString('landlordPortal.rentRoll.renewals.areServicesIncluded')}
      </BoldTitle>

      <StyledRadioUnderline>
        <RadioGroup
          name={'services.gas'}
          value={services.gas.is_included}
          onChange={val => setFieldValue('services.gas.is_included', val)}
          label={getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.gas')}
        />
      </StyledRadioUnderline>
      <StyledRadioUnderline>
        <RadioGroup
          name={'services.air_conditioning'}
          value={services.air_conditioning.is_included}
          onChange={val => setFieldValue('services.air_conditioning.is_included', val)}
          label={getTranslatedString(
            'landlordPortal.servicesAndUtilitiesWizardPage.airConditioning'
          )}
        />
      </StyledRadioUnderline>
      <StyledRadioUnderline>
        <RadioGroup
          name={'services.storage_space'}
          value={services.storage_space.is_included}
          onChange={val => setFieldValue('services.storage_space.is_included', val)}
          label={getTranslatedString(
            'landlordPortal.servicesAndUtilitiesWizardPage.additionalStorageSpace'
          )}
        />
      </StyledRadioUnderline>

      <StyledRadioUnderline expand={services.on_site_laundry.is_included}>
        <RadioGroup
          name={'services.on_site_laundry'}
          value={services.on_site_laundry.is_included}
          onChange={val => setFieldValue('services.on_site_laundry.is_included', val)}
          label={getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.onSiteLaundry')}
        />
        {services.on_site_laundry.is_included && (
          <RadioGroup
            name={'services.on_site_laundry.charge_type'}
            value={services.on_site_laundry.charge_type}
            onChange={val => {
              setFieldValue('services.on_site_laundry.charge_type', CHARGE_TYPE_MAPPING[val]);
            }}
            customOptions={[
              {
                value: 'no_charge',
                label: getTranslatedString(
                  'landlordPortal.servicesAndUtilitiesWizardPage.noCharge'
                ),
              },
              {
                value: 'pay_per_use',
                label: getTranslatedString(
                  'landlordPortal.servicesAndUtilitiesWizardPage.payPerUse'
                ),
              },
            ]}
          />
        )}
      </StyledRadioUnderline>

      <StyledRadioUnderline expand={services.guest_parking.is_included}>
        <RadioGroup
          name={'services.guest_parking'}
          value={services.guest_parking.is_included}
          onChange={val => setFieldValue('services.guest_parking.is_included', val)}
          label={getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.guestParking')}
        />
        {services.guest_parking.is_included && (
          <RadioGroup
            name={'services.guest_parking.charge_type'}
            value={services.guest_parking.charge_type}
            onChange={val => {
              setFieldValue('services.guest_parking.charge_type', CHARGE_TYPE_MAPPING[val]);
            }}
            customOptions={[
              {
                value: 'no_charge',
                label: getTranslatedString(
                  'landlordPortal.servicesAndUtilitiesWizardPage.noCharge'
                ),
              },
              {
                value: 'pay_per_use',
                label: getTranslatedString(
                  'landlordPortal.servicesAndUtilitiesWizardPage.payPerUse'
                ),
              },
            ]}
          />
        )}
      </StyledRadioUnderline>

      <AdditionalServices services={services} setFieldValue={setFieldValue} />

      <ServiceDetails
        values={values}
        errors={errors}
        touched={touched}
        setFieldValue={setFieldValue}
      />

      <BoldTitle>
        {getTranslatedString('landlordPortal.rentRoll.renewals.whoPaysForUtilities')}
      </BoldTitle>

      <StyledRadioUnderline>
        <RadioGroup
          name={'utilities.electricity'}
          value={utilities.electricity.paid_by}
          label={getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.electricity')}
          onChange={val => {
            setFieldValue('utilities.electricity.paid_by', CHARGE_TYPE_MAPPING[val]);
          }}
          customOptions={[
            {
              value: 'landlord',
              label: getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.landlord'),
            },
            {
              value: 'tenant',
              label: getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.resident'),
            },
          ]}
        />
      </StyledRadioUnderline>
      <StyledRadioUnderline>
        <RadioGroup
          name={'utilities.heat'}
          value={utilities.heat.paid_by}
          label={getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.heat')}
          onChange={val => {
            setFieldValue('utilities.heat.paid_by', CHARGE_TYPE_MAPPING[val]);
          }}
          customOptions={[
            {
              value: 'landlord',
              label: getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.landlord'),
            },
            {
              value: 'tenant',
              label: getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.resident'),
            },
          ]}
        />
      </StyledRadioUnderline>
      <StyledRadioUnderline>
        <RadioGroup
          name={'utilities.water'}
          value={utilities.water.paid_by}
          label={getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.water')}
          onChange={val => {
            setFieldValue('utilities.water.paid_by', CHARGE_TYPE_MAPPING[val]);
          }}
          customOptions={[
            {
              value: 'landlord',
              label: getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.landlord'),
            },
            {
              value: 'tenant',
              label: getTranslatedString('landlordPortal.servicesAndUtilitiesWizardPage.resident'),
            },
          ]}
        />
      </StyledRadioUnderline>

      <UtilityDetails
        values={values}
        errors={errors}
        touched={touched}
        setFieldValue={setFieldValue}
      />
    </Card>
  );
};

export default ServicesAndUtilitiesCard;
